import { registerSW } from 'virtual:pwa-register';

declare global {
  interface Window {
    updateServiceWorker?: (reloadPage?: boolean) => Promise<void>;
  }
}

// Browsers only re-check a registered service worker's script for changes on a hard
// navigation/reload -- a long-lived SPA session (or a phone left sitting in the PWA)
// can go all day without one, so onNeedRefresh below never fires and the "Reload to
// update" banner never appears, even though a new version has been deployed. Polling
// registration.update() periodically, plus re-checking whenever the tab/PWA regains
// focus (the common mobile pattern -- switching apps, not reloading), makes the
// update actually get detected during the session instead of only on next cold load.
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// skipWaiting + clientsClaim (vite.config.ts) let a newly-installed worker take over
// an ALREADY-OPEN tab's future network requests in the background, with no user
// action. But that alone doesn't refresh anything the tab already has in memory --
// the page keeps running the JS it originally loaded (missing whatever shipped in
// the new build, e.g. a new icon/component) until an actual reload happens. This is
// the standard companion piece: once a new worker takes control, reload once so the
// open tab actually picks up the new bundle instead of silently running the old one
// indefinitely. Guarded so it can only fire once per tab (controllerchange can fire
// more than once in edge cases, and reloading in a loop would be worse than the bug).
//
// clients.claim() also fires controllerchange on a page that had NO controller at
// all when it loaded -- every single first-ever visit to the site, since there's no
// prior worker yet for the newly-installed one to claim control FROM. That's not an
// update landing, there's no older version running to refresh away from, so treating
// it the same way caused a pointless full reload ~1-2s into every fresh page load
// (confirmed live: an extra navigation firing on a brand new browser context with no
// prior visit, verified via Playwright's framenavigated count). Only the "had a
// controller already, and it just changed" case is a real update.
function reloadOnNewServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;
  if (!hadControllerAtLoad) return;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}

export function initServiceWorker() {
  reloadOnNewServiceWorker();

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // Dispatch an event the app can listen to and show a "reload to update" toast.
      window.dispatchEvent(new CustomEvent('sw:update-available'));
    },
    onOfflineReady() {
      window.dispatchEvent(new CustomEvent('sw:offline-ready'));
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_CHECK_INTERVAL_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      });
    },
  });

  // Expose so a UI component can call it when the user accepts the update prompt.
  window.updateServiceWorker = updateSW;
}
